import { useState, type FormEvent } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { siteInfo } from '@/mocks/data';

const breadcrumbItems = [{ label: '首页', href: '/' }, { label: '关于我们' }];

const testimonials = [
  {
    name: '王建国',
    role: '某大型建筑集团项目经理',
    content: '使用白浆百科推荐的建筑白浆方案后，我们的墙面找平效率提升了30%，施工质量得到了甲方的高度认可。白浆百科的专业知识库是我们的重要参考来源。',
    rating: 5,
  },
  {
    name: '李明珠',
    role: '华东某造纸企业技术总监',
    content: '白浆百科对造纸白浆工艺的深度解析帮助我们的技术团队解决了多个生产难题。尤其是智能化制浆方面的文章，给了我们很多启发。',
    rating: 5,
  },
  {
    name: '陈志远',
    role: '食品加工厂质量负责人',
    content: '食品级白浆的安全标准和认证体系介绍非常全面，帮助我们建立了更完善的原料采购和验收流程。专业且可靠，值得信赖！',
    rating: 5,
  },
  {
    name: '赵晓峰',
    role: '工业涂料企业研发工程师',
    content: '白浆百科的配方定制案例给了我们很多思路。按照文章中的参数调控方法，我们成功优化了多款产品的配方，客户反馈非常好。',
    rating: 5,
  },
  {
    name: '张丽华',
    role: '建材经销商总经理',
    content: '作为一名经销商，白浆百科让我对不同品类白浆的特点和应用有了全面的了解，现在给客户推荐产品时底气更足了，销售额也上去了。',
    rating: 5,
  },
  {
    name: '刘明辉',
    role: '环保检测机构技术专家',
    content: '白浆百科在环保型白浆趋势方面的内容非常有前瞻性，引用的数据和标准都很准确。行业内能有这样一个专业的信息平台非常好。',
    rating: 5,
  },
];

const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: siteInfo.name,
  url: siteInfo.url,
  description: siteInfo.description,
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: '400-888-9999',
    contactType: 'customer service',
    availableLanguage: 'Chinese',
    email: 'contact@baijiang-baike.com',
  },
  address: {
    '@type': 'PostalAddress',
    addressLocality: '上海',
    addressRegion: '上海',
    addressCountry: 'CN',
    streetAddress: '浦东新区张江高科技园区',
  },
};

export default function About() {
  const [formState, setFormState] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('phone_alt') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormState('success');
      return;
    }

    try {
      const formData = new FormData(form);
      formData.delete('phone_alt');
      const response = await fetch('https://readdy.ai/api/form/d9snrftlbrt1qcgaj6m0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      if (response.ok && parsed?.code === 'OK') {
        setFormState('success');
        form.reset();
      } else {
        setFormState('error');
        setFormError(serverMsg || '提交失败，请重试');
      }
    } catch {
      setFormState('error');
      setFormError('网络错误，请稍后重试');
    }
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationSchema) }} />

      <Navbar />
      <ScrollToTop />

      <main className="pt-16">
        {/* Header */}
        <section className="bg-background-100 py-10 md:py-14 px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <div className="mb-6">
              <Breadcrumb items={breadcrumbItems} />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3" style={{ fontFamily: "var(--font-heading)" }}>
              关于我们
            </h1>
            <p className="text-sm text-foreground-600 max-w-2xl">
              白浆百科致力于打造最全面、最专业的白浆知识平台，为行业从业者提供权威的信息参考。
            </p>
          </div>
        </section>

        {/* Brand Introduction */}
        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-10 lg:gap-16 items-center">
              <div className="lg:w-1/2 flex-shrink-0">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20clean%20minimalist%20office%20workspace%20with%20white%20and%20cream%20tones%2C%20professional%20team%20at%20work%2C%20warm%20natural%20lighting%2C%20editorial%20photography%2C%20high%20detail%2C%20sophisticated%20aesthetic&width=800&height=600&seq=baijiang-about-01&orientation=landscape"
                  alt="白浆百科团队"
                  className="rounded-lg w-full h-72 md:h-96 object-cover"
                />
              </div>
              <div className="lg:w-1/2">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-4" style={{ fontFamily: "var(--font-heading)" }}>
                  品牌介绍
                </h2>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  白浆百科创立于2006年，是国内最早专注于白浆领域知识分享的专业平台之一。经过近二十年的深耕，我们已积累了超过<strong>500种白浆配方方案</strong>的技术数据库，服务覆盖建筑、造纸、食品加工及工业制造四大核心领域。
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  我们的团队由来自化工、材料科学、食品工程等领域的资深专家组成，致力于将复杂的白浆技术知识转化为通俗易懂的内容，帮助从业者快速获取所需信息，推动行业整体技术水平的提升。
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  白浆百科始终秉持<strong>"专业、客观、实用"</strong>的理念，所有内容均经过严格的技术审核，确保信息的准确性和可靠性。我们相信，知识的力量能够帮助每一位从业者做出更明智的决策。
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Core Values */}
        <section className="py-14 md:py-16 px-4 md:px-6 bg-secondary-50">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 text-center mb-10" style={{ fontFamily: "var(--font-heading)" }}>
              核心价值观
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {[
                { icon: 'ri-shield-check-line', title: '专业严谨', desc: '所有内容由行业专家审核，确保技术信息的准确性与权威性' },
                { icon: 'ri-lightbulb-line', title: '创新引领', desc: '持续跟踪白浆领域前沿技术动态，为行业提供前瞻性洞察' },
                { icon: 'ri-group-line', title: '用户至上', desc: '以满足从业者实际需求为核心，不断优化内容与服务体验' },
              ].map((val) => (
                <div key={val.title} className="bg-background-50 border border-background-200/70 rounded-lg p-6 text-center">
                  <div className="w-12 h-12 rounded-full bg-accent-100 flex items-center justify-center mx-auto mb-4">
                    <i className={`${val.icon} text-xl text-accent-600`}></i>
                  </div>
                  <h3 className="font-semibold text-foreground-950 mb-2 text-sm" style={{ fontFamily: "var(--font-heading)" }}>
                    {val.title}
                  </h3>
                  <p className="text-xs text-foreground-600 leading-relaxed">{val.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 text-center mb-10" style={{ fontFamily: "var(--font-heading)" }}>
              行业口碑
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {testimonials.map((t) => (
                <div key={t.name} className="bg-background-100 border border-background-200/70 rounded-lg p-5">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-sm text-accent-500"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    &ldquo;{t.content}&rdquo;
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center">
                      <span className="text-xs font-semibold text-primary-600">{t.name.charAt(0)}</span>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-950">{t.name}</div>
                      <div className="text-xs text-foreground-500">{t.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-10 lg:gap-16">
              <div className="lg:w-2/5 flex-shrink-0">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-4" style={{ fontFamily: "var(--font-heading)" }}>
                  联系我们
                </h2>
                <p className="text-sm text-foreground-600 leading-relaxed mb-8">
                  如果您对白浆产品有任何疑问，或有合作意向，欢迎随时与我们联系。我们的专业团队将竭诚为您服务。
                </p>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-primary-600"></i>
                    </div>
                    <div>
                      <div className="text-xs text-foreground-500 mb-0.5">电话</div>
                      <div className="text-sm font-medium text-foreground-950">400-888-9999</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-primary-600"></i>
                    </div>
                    <div>
                      <div className="text-xs text-foreground-500 mb-0.5">邮箱</div>
                      <div className="text-sm font-medium text-foreground-950">contact@baijiang-baike.com</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-primary-600"></i>
                    </div>
                    <div>
                      <div className="text-xs text-foreground-500 mb-0.5">地址</div>
                      <div className="text-sm font-medium text-foreground-950">中国上海市浦东新区张江高科技园区</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-time-line text-primary-600"></i>
                    </div>
                    <div>
                      <div className="text-xs text-foreground-500 mb-0.5">工作时间</div>
                      <div className="text-sm font-medium text-foreground-950">周一至周五 9:00 - 18:00</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="lg:w-3/5">
                <div className="bg-background-50 border border-background-200/70 rounded-lg p-6">
                  <h3 className="font-semibold text-foreground-950 mb-5 text-sm" style={{ fontFamily: "var(--font-heading)" }}>
                    发送消息
                  </h3>
                  <form onSubmit={handleSubmit} data-readdy-form="">
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-700 mb-1.5">
                            姓名 <span className="text-red-500">*</span>
                          </label>
                          <input
                            id="contact-name"
                            name="name"
                            type="text"
                            required
                            className="w-full px-3 py-2.5 border border-background-200/70 rounded-md text-sm text-foreground-950 bg-background-50 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400"
                            placeholder="请输入您的姓名"
                          />
                        </div>
                        <div>
                          <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-700 mb-1.5">
                            邮箱 <span className="text-red-500">*</span>
                          </label>
                          <input
                            id="contact-email"
                            name="email"
                            type="email"
                            required
                            className="w-full px-3 py-2.5 border border-background-200/70 rounded-md text-sm text-foreground-950 bg-background-50 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400"
                            placeholder="请输入您的邮箱"
                          />
                        </div>
                      </div>
                      <div>
                        <label htmlFor="contact-subject" className="block text-xs font-medium text-foreground-700 mb-1.5">
                          主题 <span className="text-red-500">*</span>
                        </label>
                        <input
                          id="contact-subject"
                          name="subject"
                          type="text"
                          required
                          className="w-full px-3 py-2.5 border border-background-200/70 rounded-md text-sm text-foreground-950 bg-background-50 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400"
                          placeholder="请输入消息主题"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-700 mb-1.5">
                          消息内容 <span className="text-red-500">*</span>
                        </label>
                        <textarea
                          id="contact-message"
                          name="message"
                          required
                          rows={5}
                          maxLength={500}
                          className="w-full px-3 py-2.5 border border-background-200/70 rounded-md text-sm text-foreground-950 bg-background-50 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 resize-none"
                          placeholder="请输入您的消息内容（不超过500字符）"
                        ></textarea>
                      </div>
                      <input
                        type="text"
                        name="phone_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                        className="absolute opacity-0 pointer-events-none"
                        style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}
                      />
                      <button
                        type="submit"
                        className="w-full px-6 py-3 bg-primary-500 text-background-50 rounded-md font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        发送消息
                      </button>
                    </div>
                    {formState === 'success' && (
                      <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-md">
                        <p className="text-sm text-green-700">消息已发送成功！我们会尽快与您联系。</p>
                      </div>
                    )}
                    {formState === 'error' && (
                      <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
                        <p className="text-sm text-red-700">{formError}</p>
                      </div>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}